import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getLabelForRoute } from './useLastVisitedRoute';

/**
 * Tracks a back/forward stack for the enclosing router.
 *
 * @param navigate  optional react-router navigate(). Pass it when the enclosing
 *                  router is a tab's MemoryRouter, which has its own history and
 *                  is not driven by window.history. Omit it in the untabbed
 *                  shell, where the BrowserRouter *is* window.history.
 */
export default function useNavigationHistory(navigate) {
  const location = useLocation();
  const stackRef = useRef([]);
  const indexRef = useRef(-1);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [currentLabel, setCurrentLabel] = useState('');

  useEffect(() => {
    const { key, pathname } = location;
    const stack = stackRef.current;
    const idx = indexRef.current;

    if (idx >= 0 && stack[idx] && stack[idx].key === key) {
      setCurrentLabel(getLabelForRoute(pathname));
      return;
    }

    if (idx > 0 && stack[idx - 1] && stack[idx - 1].key === key) {
      indexRef.current = idx - 1;
    } else if (idx < stack.length - 1 && stack[idx + 1] && stack[idx + 1].key === key) {
      indexRef.current = idx + 1;
    } else {
      stackRef.current = stack.slice(0, idx + 1);
      stackRef.current.push({ key, pathname });
      indexRef.current = stackRef.current.length - 1;
    }

    const newIdx = indexRef.current;
    setCanGoBack(newIdx > 0);
    setCanGoForward(newIdx < stackRef.current.length - 1);
    setCurrentLabel(getLabelForRoute(pathname));
  }, [location]);

  const goBack = useCallback(() => {
    if (indexRef.current <= 0) return;
    if (navigate) navigate(-1);
    else window.history.back();
  }, [navigate]);

  const goForward = useCallback(() => {
    if (indexRef.current >= stackRef.current.length - 1) return;
    if (navigate) navigate(1);
    else window.history.forward();
  }, [navigate]);

  return { canGoBack, canGoForward, goBack, goForward, currentLabel };
}
